import type React from 'react';
import { useState } from 'react';
import {
  ChevronDown,
  HelpCircle,
  Languages,
  LogOut,
  Store,
} from 'lucide-react';
import { Link } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Separator } from '@/components/ui/separator';
import { MenuTag } from '@/components/menu-tag';
import { useChatwootWidget } from '@/components/chatwoot-widget/chatwoot-widget-context';
import { useLocaleSwitcher } from '@/components/language-selector';
import { useActiveSubscription } from '@/hooks/use-billing';
import { useEnrolledTenants } from '@/hooks/use-enrolled-tenants';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useLogout } from '@/hooks/use-logout';
import { useTenant } from '@/hooks/use-tenant';
import { getSupportedLocales } from '@/lib/locale-utils';
import { getMoreItems } from '@/lib/navigation';
import { cn } from '@/lib/utils';
import { NAV_ITEM_ICONS, useNavItemLabel } from './nav-items';

export interface MoreSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Closes the Mais and opens the StoreSwitcherSheet. */
  onSwitchStore: () => void;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}

const rowClassName =
  'flex min-h-touch w-full items-center gap-3 rounded-lg px-3 text-left text-sm font-medium outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring';

/**
 * Mais sheet (SHELL-02): store card, the "Loja" destinations, Ajuda, Idioma
 * (options listed in the sheet itself) and Sair.
 */
export function MoreSheet({
  open,
  onOpenChange,
  onSwitchStore,
  returnFocusRef,
}: MoreSheetProps) {
  const { LL } = useI18nContext();
  const labelOf = useNavItemLabel();
  const tenant = useTenant();
  const { data: tenants } = useEnrolledTenants();
  const { data: subscription } = useActiveSubscription();
  const { data: featureFlags } = useFeatureFlags();
  const { setIsOpen: openHelp } = useChatwootWidget();
  const logout = useLogout();
  const { locale, isChanging, changeLocale } = useLocaleSwitcher();
  const [languagesOpen, setLanguagesOpen] = useState(false);

  const locales = getSupportedLocales();
  const currentLocaleName = locales.find((l) => l.code === locale)?.name;
  const canSwitchStore = (tenants?.length ?? 0) > 1;
  const items = getMoreItems({
    tenantSlug: tenant.slug,
    audiencesEnabled: featureFlags?.audiencesEnabled ?? false,
    hasActiveSubscription: !!subscription,
    fromShopifyAppStore: tenant.fromShopifyAppStore,
  });

  const close = () => onOpenChange(false);

  const chooseLocale = (code: Locales) => {
    setLanguagesOpen(false);
    void changeLocale(code);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent returnFocusRef={returnFocusRef}>
        <DrawerTitle className="sr-only">{LL.shell.more.title()}</DrawerTitle>
        <DrawerDescription className="sr-only">
          {LL.shell.more.description()}
        </DrawerDescription>

        <div className="overflow-y-auto px-4 pb-4">
          <div className="flex items-center gap-3 rounded-mobile-card border p-3">
            <Avatar className="size-10 rounded-lg">
              <AvatarImage src={tenant.iconUrl} alt="" />
              <AvatarFallback className="rounded-lg">
                <Store className="size-4" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{tenant.name}</p>
              <p className="truncate text-sm text-muted-foreground">
                {tenant.ecommercePlatformReadableName}
              </p>
            </div>
            {canSwitchStore && (
              <Button
                type="button"
                variant="outline"
                className="min-h-touch"
                onClick={onSwitchStore}
              >
                {LL.shell.more.switchStore()}
              </Button>
            )}
          </div>

          <nav className="mt-2 flex flex-col">
            {items.map((item) => {
              const Icon = NAV_ITEM_ICONS[item.id];
              return (
                <Link
                  key={item.id}
                  to={item.href}
                  onClick={close}
                  className={rowClassName}
                >
                  <Icon className="size-5 text-muted-foreground" />
                  <span>{labelOf(item.id)}</span>
                  {item.soonTag && (
                    <MenuTag text={LL.common.soon()} color="bg-blue-500" />
                  )}
                </Link>
              );
            })}
          </nav>

          <Separator className="my-2" />

          <div className="flex flex-col">
            <button
              type="button"
              className={rowClassName}
              onClick={() => {
                close();
                openHelp(true);
              }}
            >
              <HelpCircle className="size-5 text-muted-foreground" />
              <span>{LL.helpPage.title()}</span>
            </button>

            <button
              type="button"
              className={rowClassName}
              aria-expanded={languagesOpen}
              disabled={isChanging}
              onClick={() => setLanguagesOpen((value) => !value)}
            >
              <Languages className="size-5 text-muted-foreground" />
              <span>{LL.shell.more.language()}</span>
              <span className="ml-auto flex items-center gap-1 font-normal text-muted-foreground">
                {currentLocaleName}
                <ChevronDown
                  className={cn(
                    'size-4 transition-transform',
                    languagesOpen && 'rotate-180'
                  )}
                />
              </span>
            </button>
            {languagesOpen && (
              <div className="flex flex-col pl-8">
                {locales.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    aria-pressed={option.code === locale}
                    className={cn(rowClassName, 'aria-pressed:text-primary')}
                    onClick={() => chooseLocale(option.code)}
                  >
                    {option.name}
                  </button>
                ))}
              </div>
            )}

            <button
              type="button"
              className={rowClassName}
              onClick={() => void logout()}
            >
              <LogOut className="size-5 text-muted-foreground" />
              <span>{LL.logout()}</span>
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
